import { useEffect, useRef, useState } from 'react';

type UseChatFileDropOptions = {
  /** False while the composer is locked (generation, dictation, quota): no overlay, no drop. */
  enabled: boolean;
  onFiles: (files: File[]) => void;
};

function isFileDrag(event: DragEvent) {
  return Array.from(event.dataTransfer?.types ?? []).includes('Files');
}

function isOutsideViewport(event: DragEvent) {
  return event.clientX <= 0
    || event.clientY <= 0
    || event.clientX >= window.innerWidth
    || event.clientY >= window.innerHeight;
}

/**
 * Window-wide file drop zone for the conversation page.
 *
 * - Only drags carrying files count (`dataTransfer.types` includes "Files"): dragging text
 *   or a link never shows the overlay.
 * - A dragenter/dragleave depth counter avoids flicker when the pointer crosses inner elements;
 *   leaving the window also resets it (Safari reports a null `relatedTarget` on every dragleave).
 * - A file dropped anywhere is always `preventDefault`-ed so the browser never opens it.
 * - The drop listener runs in the capture phase and stops propagation: the composer's
 *   `PromptInput` registers its own native drop listener that would add every dropped file to
 *   its internal (unfiltered) attachment list and base64-encode it at submit time.
 */
export function useChatFileDrop({ enabled, onFiles }: UseChatFileDropOptions) {
  const [isDragging, setIsDragging] = useState(false);
  const enabledRef = useRef(enabled);
  const onFilesRef = useRef(onFiles);

  useEffect(() => {
    enabledRef.current = enabled;
    onFilesRef.current = onFiles;
  }, [enabled, onFiles]);

  useEffect(() => {
    let depth = 0;

    function reset() {
      depth = 0;
      setIsDragging(false);
    }

    function handleDragEnter(event: DragEvent) {
      if (!isFileDrag(event)) {
        return;
      }
      event.preventDefault();
      depth += 1;
      if (enabledRef.current) {
        setIsDragging(true);
      }
    }

    function handleDragOver(event: DragEvent) {
      if (!isFileDrag(event)) {
        return;
      }
      // Required to be a valid drop target; "none" shows the not-allowed cursor while locked.
      event.preventDefault();
      if (event.dataTransfer) {
        event.dataTransfer.dropEffect = enabledRef.current ? 'copy' : 'none';
      }
      if (enabledRef.current) {
        setIsDragging(true);
      }
    }

    function handleDragLeave(event: DragEvent) {
      if (!isFileDrag(event)) {
        return;
      }
      depth = Math.max(0, depth - 1);
      if (depth === 0 || isOutsideViewport(event)) {
        reset();
      }
    }

    function handleDrop(event: DragEvent) {
      if (!isFileDrag(event)) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      reset();

      const files = Array.from(event.dataTransfer?.files ?? []);
      if (enabledRef.current && files.length > 0) {
        onFilesRef.current(files);
      }
    }

    const listeners = [
      ['dragenter', handleDragEnter],
      ['dragover', handleDragOver],
      ['dragleave', handleDragLeave],
      ['drop', handleDrop],
      ['dragend', reset],
    ] as const;

    for (const [type, listener] of listeners) {
      window.addEventListener(type, listener as EventListener, true);
    }

    return () => {
      for (const [type, listener] of listeners) {
        window.removeEventListener(type, listener as EventListener, true);
      }
    };
  }, []);

  return { isDragging: isDragging && enabled };
}
